import { useEffect, useRef } from 'react';

/**
 * Componente de Cloudflare Turnstile (Captcha)
 * 
 * @param {function} onVerify - Callback que recibe el token cuando el usuario completa el captcha
 * @param {function} onError - Callback cuando hay un error
 * @param {function} onExpire - Callback cuando el token expira
 */
export default function TurnstileWidget({ onVerify, onError, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    
    // Si no hay site key configurada, no mostrar el widget
    if (!siteKey) {
      console.warn('⚠️ VITE_TURNSTILE_SITE_KEY no configurada');
      return;
    }

    // Prevenir inicialización múltiple
    if (isInitializedRef.current) {
      return;
    }

    // Esperar a que el script de Turnstile se cargue
    const initTurnstile = () => {
      if (window.turnstile && containerRef.current && !isInitializedRef.current) {
        isInitializedRef.current = true;
        
        // Renderizar el widget
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            console.log('✅ Turnstile verificado');
            onVerify?.(token);
          },
          'error-callback': () => {
            console.error('❌ Error en Turnstile');
            onError?.();
          },
          'expired-callback': () => {
            console.warn('⏰ Turnstile expirado');
            onExpire?.();
          },
          theme: 'light',
          size: 'normal',
        });
      }
    };

    // Si el script ya está cargado, inicializar inmediatamente
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Si no, esperar a que se cargue
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 100);

      return () => clearInterval(checkTurnstile);
    }

    // Cleanup: remover el widget cuando el componente se desmonte
    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, []);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  
  // Si no hay site key, no renderizar nada
  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} className="flex justify-center my-4"></div>;
}
